import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { CALL } from '@/constants/testIds';
import { useLanguage } from '../context/LanguageContext';

export const CaptionHistoryPanel = ({ open, onClose, history, isMobile = false }) => {
  const { t } = useLanguage();
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [history.length]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-30 bg-black/40"
            />
          )}
          <motion.aside
            data-testid={CALL.captionPanel}
            initial={isMobile ? { y: '100%', opacity: 0 } : { x: '100%', opacity: 0 }}
            animate={isMobile ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
            exit={isMobile ? { y: '100%', opacity: 0 } : { x: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={
              isMobile
                ? 'fixed inset-x-0 bottom-0 z-30 flex max-h-[60vh] flex-col rounded-t-3xl border-t border-slate-200 bg-white shadow-2xl'
                : 'absolute right-0 top-0 z-10 flex h-full w-80 flex-col border-l border-slate-200 bg-white/95 backdrop-blur-xl'
            }
          >
            {isMobile && <div className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-slate-200" />}
            <div className="flex items-center justify-between px-5 py-5">
              <h3 className="font-heading text-lg font-semibold text-slate-900">
                {t('call.captionHistoryTitle', history.length)}
              </h3>
              <button
                data-testid={CALL.captionPanelCloseBtn}
                onClick={onClose}
                aria-label={t('call.closeCaptionPanel')}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
              {history.length === 0 && (
                <p className="mt-6 text-center font-body text-sm text-slate-400">{t('call.noCaptions')}</p>
              )}
              {history.map((cap, idx) => (
                <div
                  key={`${cap.ts}-${idx}`}
                  data-testid={CALL.captionHistoryItem(idx)}
                  translate="no"
                  className="notranslate rounded-xl bg-slate-50 px-3 py-2.5"
                >
                  <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-slate-400">{cap.speaker}</p>
                  {cap.original && <p className="font-body text-sm text-slate-500">{cap.original}</p>}
                  <p className="mt-0.5 font-body text-sm font-medium text-slate-800">{cap.translated}</p>
                </div>
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
